import { useCallback, useEffect, useMemo, useState } from "react";
import ModalFullScreen from "components/common/ModalFullScreen";
import { usePagination } from "components/common/BottomPageSelectorWithPagination";
import { useMessaging } from "context/MessagingContext";
import { useTranslation } from "react-i18next";
import usePriceListMutations from "graphql/usePriceListMutations";
import useItems from "graphql/useItems";
import usePriceLists from "graphql/usePriceLists";
import { BSON } from "realm-web";
import useSubcategories, { useCategories } from "graphql/useSubcategories";
import AddList from "./AddList";
import TopMenu from "./TopMenu";
import s from "./styles";
import _ from "lodash";
import useItemMutations from "graphql/useItemMutations";
import PriceTable from "./PriceTable";
import ListTable from "./ListTable";
import Loading from "components/common/loading";

function InventoryPriceList() {
  const { t } = useTranslation();
  const [itemToEdit, setItemToEdit] = useState(null);

  const { updatePriceList } = usePriceListMutations();
  const { updateItem } = useItemMutations();

  const { setInfoMessage, setConfirmMessage, setErrorMessage } = useMessaging();

  const { search, setSearch, pagination, setPagination } =
    usePagination("name");

  function changeSearch(v) {
    setSearch(v);
    setPagination({ ...pagination, page: 0 });
  }

  const { priceLists, loading: loading2 } = usePriceLists();
  const { subcategories, loading: loading3 } = useSubcategories();
  const { categories, loading: loading4 } = useCategories();

  const { items, loading } = useItems();

  const [addModifierModalOpen, setAddItemModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState("prices");
  const [editing, setEditing] = useState(false);

  const [selectList, setSelectList] = useState(null);
  const [selectCategory, setSelectCategory] = useState(null);
  const [selectSubcategory, setSelectSubcategory] = useState(null);
  const [hasEdits, setHasEdits] = useState(false);

  const mapOptions = (options, namePrefix) => {
    const mappedOptions = options.map(({ _id, name }) => ({
      name: `${namePrefix} ${name}`,
      value: _id,
    }));
    return mappedOptions;
  };

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 0 }));
  }, [search, selectCategory, selectSubcategory, selectList]);

  const { priceListsOptions, categoriesListOptions, subCategoriesListOptions } =
    useMemo(() => {
      const sub =
        subcategories?.filter(
          (subcategory) => subcategory.categoryId === selectCategory
        ) || [];

      return {
        priceListsOptions: [
          { name: t("containers.InventoryPriceList.AllList"), value: null },
          {
            name: t("containers.InventoryPriceList.defaultPriceList"),
            value: "default",
          },
          ...mapOptions(priceLists, ""),
        ],
        categoriesListOptions: [
          {
            name: t("containers.InventoryPriceList.AllCategories"),
            value: null,
          },
          ...mapOptions(categories, ""),
        ],
        subCategoriesListOptions: [
          {
            name: t("containers.InventoryPriceList.AllSubCategories"),
            value: null,
          },
          ...mapOptions(sub, ""),
        ],
      };
    }, [subcategories, priceLists, categories, selectCategory]);

  const [priceListsForEdit, setPriceListsForEdit] = useState(priceLists);

  const defaultPriceList = useMemo(() => {
    const itemsMap = items.map((item) => ({
      itemId: item._id,
      price: item.price,
      _id: new BSON.ObjectId().toString(),
    }));

    return {
      _id: "default",
      name: t("containers.InventoryPriceList.defaultPriceList"),
      items: itemsMap,
      updateDatetime: new Date().toISOString(),
      creationDatetime: new Date().toISOString(),
    };
  }, [items, t]);

  useEffect(() => {
    setPriceListsForEdit([defaultPriceList, ...priceLists]);
  }, [priceLists, defaultPriceList]);

  const onUpdateSuccess = useCallback(() => {
    setInfoMessage({
      titleAlert: t("containers.InventoryPriceList.listEdit"),
      info1: t("containers.InventoryPriceList.findList"),
    });
  }, [setInfoMessage, t]);

  const onAddSuccess = useCallback(() => {
    setInfoMessage({
      titleAlert: t("containers.InventoryPriceList.newList"),
      info1: t("containers.InventoryPriceList.listDB"),
    });
  }, [setInfoMessage, t]);

  const onUpdatePrices = useCallback(() => {
    setSaving(true);

    const handleUpdatePriceList = async () => {
      const modifiedPriceLists = priceListsForEdit.filter((editedList) => {
        const originalList = [defaultPriceList, ...priceLists].find(
          (list) => list._id === editedList._id
        );
        return !_.isEqual(originalList, editedList);
      });
      try {
        await Promise.all(
          modifiedPriceLists.map(async (list) => {
            list._id !== "default"
              ? await updateNonDefaultPriceList(list)
              : await updateDefaultPriceList();
          })
        );
        onUpdateSuccess();
        setEditing(false);
      } catch (e) {
        setErrorMessage({ error: e });
      } finally {
        setSaving(false);
      }
    };

    const updateNonDefaultPriceList = async (list) => {
      try {
        const listOriginal = priceLists.find((list2) => list2._id === list._id);
        const cleanList = {
          creationDatetime: list.creationDatetime,
          name: list.name,
          _id: list._id,
          items: list.items.map(({ itemId, price, _id }) => ({
            itemId,
            price,
            _id,
          })),
        };

        await updatePriceList(listOriginal, cleanList);
      } catch (e) {
        setErrorMessage({ error: e });
      }
    };

    const updateDefaultPriceList = async () => {
      try {
        const defaultPriceListToEdit = priceListsForEdit.find(
          (list) => list._id === "default"
        );

        if (!defaultPriceListToEdit) return;

        const mismatchedItems = items.filter((currentItem) => {
          const defaultItem = defaultPriceListToEdit.items.find(
            (item) => item.itemId === currentItem._id
          );
          return defaultItem && defaultItem.price !== currentItem.price;
        });

        await Promise.all(
          mismatchedItems.map(async (currentItem) => {
            const defaultItem = defaultPriceListToEdit.items.find(
              (item) => item.itemId === currentItem._id
            );

            if (defaultItem) {
              const { __typename, ...cleanList } = currentItem;

              const updatedItem = {
                ...cleanList,
                price: defaultItem.price,
              };

              await updateItem(cleanList, updatedItem);
            }
          })
        );
      } catch (e) {
        setErrorMessage({ error: e });
      }
    };

    setConfirmMessage({
      titleAlert: t("containers.InventoryPriceList.updatePriceList"),
      confirm1: t("containers.InventoryPriceList.updatePriceListMsj"),
      onConfirm: handleUpdatePriceList,
      onCancel: () => [
        setEditing(false),
        setSaving(false),

        setPriceListsForEdit([defaultPriceList, ...priceLists]),
      ],
    });
  }, [
    priceListsForEdit,
    items,
    updatePriceList,
    updateItem,
    setConfirmMessage,
    t,
    onUpdateSuccess,
    setEditing,
    setErrorMessage,
    setSaving,
  ]);

  const handleSetPriceListsForEdit = (updatedPriceLists) => {
    setPriceListsForEdit(updatedPriceLists);
    setHasEdits(
      !_.isEqual([defaultPriceList, ...priceLists], updatedPriceLists)
    );
  };

  const loadingAll = loading || loading2 || loading3 || loading4;

  return (
    <s.PageContainer>
      <TopMenu
        changeSearchValue={(v) => changeSearch(v)}
        searchValue={search}
        addNewElement={() => setAddItemModalOpen(true)}
        view={view}
        setView={setView}
        editing={editing}
        setEditing={setEditing}
        loading={loadingAll}
        priceListsOptions={priceListsOptions}
        selectList={selectList}
        setSelectList={setSelectList}
        selectCategory={selectCategory}
        setSelectCategory={setSelectCategory}
        selectSubcategory={selectSubcategory}
        setSelectSubcategory={setSelectSubcategory}
        categoriesListOptions={categoriesListOptions}
        subCategoriesListOptions={subCategoriesListOptions}
        handleUpdatePriceList={onUpdatePrices}
        hasEdits={hasEdits}
        saving={saving}
      />

      {loading ? (
        <Loading />
      ) : (
        <s.ListContainer>
          {view === "prices" ? (
            <PriceTable
              priceLists={priceLists}
              selectList={selectList}
              search={search}
              selectSubcategory={selectSubcategory}
              selectCategory={selectCategory}
              editing={editing}
              priceListsForEdit={priceListsForEdit}
              setPriceListsForEdit={handleSetPriceListsForEdit}
            />
          ) : (
            <ListTable
              setItemToEdit={setItemToEdit}
              setAddItemModalOpen={setAddItemModalOpen}
              search={search}
            />
          )}
        </s.ListContainer>
      )}
      {addModifierModalOpen && (
        <ModalFullScreen>
          <AddList
            closeModal={() => {
              setAddItemModalOpen(false);
              setItemToEdit(null);
            }}
            editItem={itemToEdit}
            onUpdateSuccess={onUpdateSuccess}
            onAddSuccess={onAddSuccess}
            setErrorMessage={setErrorMessage}
            priceListsOptions={priceListsOptions}
            priceLists={priceLists}
            items={items}
          />
        </ModalFullScreen>
      )}
    </s.PageContainer>
  );
}

export default InventoryPriceList;
